import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { money, fmtDate, initials } from "@/lib/format";
import { StatusBadge } from "@/components/shared";
import CustomerDialog from "@/components/CustomerDialog";
import StageSelect from "@/components/StageSelect";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  ArrowLeft, Phone, Mail, Building2, Pencil, Trash2, Plus, PhoneCall,
  FileText, StickyNote, CalendarIcon, Loader2, MessageSquare,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared";

const TYPE_META = {
  call: { label: "Call", icon: PhoneCall },
  email: { label: "Email", icon: Mail },
  note: { label: "Note", icon: StickyNote },
  enquiry: { label: "Enquiry", icon: FileText },
};

function LogDialog({ customerId, open, onOpenChange, onDone }) {
  const [type, setType] = useState("call");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await client.post(`/customers/${customerId}/interactions`, {
        type,
        note: note || null,
        next_action_date: date || null,
      });
      toast.success("Interaction logged");
      setNote(""); setDate(null); setType("call");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log interaction");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="log-interaction-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Log interaction</DialogTitle><DialogDescription>Record a call, email or note and optionally set the next follow-up date.</DialogDescription></DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5">
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="log-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="call">Phone call</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="note">Note</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Details</Label>
            <Textarea data-testid="log-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What was discussed?" />
          </div>
          <div className="grid gap-1.5">
            <Label>Set next follow-up (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="log-followup-trigger" className="justify-start font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(parseISO(date), "d MMM yyyy") : "No date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={date ? parseISO(date) : undefined} onSelect={(d) => setDate(d ? format(d, "yyyy-MM-dd") : null)} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="log-save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [creatingEnq, setCreatingEnq] = useState(false);
  const [feedFilter, setFeedFilter] = useState("all");

  const customer = useQuery({ queryKey: ["customer", id], queryFn: async () => (await client.get(`/customers/${id}`)).data });
  const enquiries = useQuery({ queryKey: ["enquiries", id], queryFn: async () => (await client.get(`/customers/${id}/enquiries`)).data });
  const interactions = useQuery({ queryKey: ["interactions", id], queryFn: async () => (await client.get(`/customers/${id}/interactions`)).data });

  const c = customer.data;

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["customer", id] });
    qc.invalidateQueries({ queryKey: ["interactions", id] });
    qc.invalidateQueries({ queryKey: ["customers"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
  };

  const startEnquiry = async () => {
    setCreatingEnq(true);
    try {
      const { data } = await client.post(`/customers/${id}/enquiries`, { title: "Showroom Visit", tax_rate: 10 });
      qc.invalidateQueries({ queryKey: ["enquiries", id] });
      qc.invalidateQueries({ queryKey: ["customer", id] });
      navigate(`/enquiries/${data.id}`);
    } catch {
      toast.error("Could not start enquiry");
    } finally {
      setCreatingEnq(false);
    }
  };

  const removeCustomer = async () => {
    try {
      await client.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      navigate("/pipeline");
    } catch {
      toast.error("Could not delete customer");
    }
  };

  if (customer.isLoading) {
    return <div className="h-64 animate-pulse rounded-2xl bg-muted/50" />;
  }
  if (customer.isError) {
    return <ErrorState onRetry={() => customer.refetch()} message="We couldn't load this customer. Please try again." />;
  }
  if (!c) {
    return <div className="py-20 text-center text-muted-foreground">Customer not found.</div>;
  }

  // Build a merged timeline from enquiries + interactions
  const timeline = [
    ...(enquiries.data || []).map((e) => ({
      key: e.id, kind: "enquiry", date: e.created_at, title: e.title,
      subtitle: `${e.items?.length || 0} items · by ${e.created_by_name || "team"}`, link: `/enquiries/${e.id}`,
    })),
    ...(interactions.data || []).map((i) => ({
      key: i.id, kind: i.type, date: i.created_at, title: TYPE_META[i.type]?.label || "Note",
      subtitle: i.note || `Logged by ${i.created_by_name || "team"}`,
    })),
  ].sort((a, b) => String(b.date).localeCompare(String(a.date)));

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> Back to pipeline
      </button>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: profile + actions */}
        <div className="space-y-4 lg:col-span-1">
          <div className="rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]">
            <div className="flex items-start gap-3">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-accent text-accent-foreground font-semibold">{initials(c.name)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <h1 className="truncate font-display text-xl font-extrabold text-ink" data-testid="customer-name">{c.name}</h1>
                <div className="mt-1.5"><StageSelect customerId={c.id} status={c.status} size="md" onChanged={refetchAll} /></div>
              </div>
            </div>

            <div className="mt-5 space-y-3 text-sm">
              {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2.5 text-ink hover:text-primary"><Phone className="h-4 w-4 text-muted-foreground" />{c.phone}</a>}
              {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2.5 text-ink hover:text-primary"><Mail className="h-4 w-4 text-muted-foreground" />{c.email}</a>}
              {c.company && <div className="flex items-center gap-2.5 text-ink"><Building2 className="h-4 w-4 text-muted-foreground" />{c.company}</div>}
              <div className="flex items-center gap-2.5 text-ink"><CalendarIcon className="h-4 w-4 text-muted-foreground" />Next follow-up: {fmtDate(c.follow_up_date)}</div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 border-t border-border pt-4">
              <div><div className="text-xs text-muted-foreground">Enquiries</div><div className="font-display text-lg font-bold text-ink">{c.enquiry_count}</div></div>
              <div><div className="text-xs text-muted-foreground">Pipeline value</div><div className="font-display text-lg font-bold text-ink">{money(c.total_value)}</div></div>
            </div>

            {c.notes && <div className="mt-4 rounded-lg bg-muted/60 p-3 text-sm text-ink">{c.notes}</div>}

            <div className="mt-5 flex flex-wrap gap-2">
              <Button onClick={() => setEdit(true)} variant="outline" size="sm" data-testid="edit-customer-btn" className="gap-1.5"><Pencil className="h-3.5 w-3.5" />Edit</Button>
              <Button onClick={() => setLogOpen(true)} variant="outline" size="sm" data-testid="log-interaction-btn" className="gap-1.5"><MessageSquare className="h-3.5 w-3.5" />Log</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="delete-customer-btn" className="gap-1.5 text-error hover:text-error"><Trash2 className="h-3.5 w-3.5" />Delete</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                    <AlertDialogDescription>This permanently removes the customer and all their enquiries. This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={removeCustomer} data-testid="confirm-delete-btn" className="bg-error hover:bg-error/90">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>

        {/* Right: enquiries + timeline */}
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-base font-bold text-ink">Enquiries & quotes</h2>
              <Button onClick={startEnquiry} disabled={creatingEnq} size="sm" data-testid="new-enquiry-btn" className="gap-1.5">
                {creatingEnq ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}New enquiry
              </Button>
            </div>
            {(enquiries.data || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No enquiries yet. Start one to build a quote.</p>
            ) : (
              <div className="space-y-2">
                {enquiries.data.map((e) => {
                  const sub = (e.items || []).reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0);
                  const total = e.gst_inclusive ? sub : sub * (1 + (e.tax_rate || 0) / 100);
                  return (
                    <Link key={e.id} to={`/enquiries/${e.id}`} data-testid={`enquiry-row-${e.id}`}
                      className="flex items-center justify-between rounded-lg border border-border p-3 transition-colors hover:border-primary/40 hover:bg-muted/40">
                      <div className="flex items-center gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-lg bg-accent text-accent-foreground"><FileText className="h-4 w-4" /></div>
                        <div>
                          <div className="text-sm font-semibold text-ink">{e.title}</div>
                          <div className="text-xs text-muted-foreground">{fmtDate(e.created_at)} · {e.items?.length || 0} items</div>
                        </div>
                      </div>
                      <div className="font-display text-sm font-bold text-ink">{money(total)}</div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-base font-bold text-ink">Interaction history</h2>
              <div className="flex flex-wrap gap-1.5">
                {[["all", "All"], ["call", "Calls"], ["email", "Emails"], ["quote", "Quotes"], ["note", "Notes"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setFeedFilter(k)} data-testid={`feed-filter-${k}`}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${feedFilter === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-white text-muted-foreground hover:text-ink"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            {timeline.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No activity yet. Log a call, email or note.</p>
            ) : (
              <ol className="relative ml-1 border-l border-border pl-5">
                {(feedFilter === "all" ? timeline : timeline.filter((t) => (feedFilter === "quote" ? t.kind === "enquiry" : t.kind === feedFilter))).map((t) => {
                  const Icon = TYPE_META[t.kind]?.icon || StickyNote;
                  return (
                    <li key={t.key} className="mb-5 last:mb-0" data-testid={`timeline-${t.key}`}>
                      <span className="absolute -left-[9px] grid h-4 w-4 place-items-center rounded-full border-2 border-white bg-primary">
                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                      </span>
                      <div className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-primary" />
                        <span className="text-sm font-semibold text-ink">{t.title}</span>
                        <span className="text-xs text-muted-foreground">· {fmtDate(t.date, "d MMM yyyy")}</span>
                      </div>
                      {t.subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{t.subtitle}</p>}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </div>

      <CustomerDialog open={edit} onOpenChange={setEdit} customer={c} onSaved={refetchAll} />
      <LogDialog customerId={id} open={logOpen} onOpenChange={setLogOpen} onDone={refetchAll} />
    </div>
  );
}
